import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { parse, stringify } from 'qs'
import { Tooltip } from '@mui/material'

import { TableUrlPagination } from '../../../../ui'
import { PUBLIC_OFFER_TABLE_COLUMNS, DEFAULT_QUERY } from './public-offer-table.constants'
import { usePublicOfferContext } from '../../contexts'
import { publicOfferDataPreparation } from '../../logics'
import { StatePublicOfferStateSwitch, TableActions } from '../table-actions'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const PublicOfferTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useSelector((state) => state.combos)
  const {
    data,
    pagination: { total_ofertas_publicas: totalEntries },
    loading,
    search,
    onClickTableRow,
    setSelectedTableRows,
    getTableData,
  } = usePublicOfferContext()

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getTableData(searchParams)
    } else {
      navigate(`/public-offer?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getTableData, navigate])

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      PUBLIC_OFFER_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.public.offer.table.columns.${column.field}.title` }),
          size: column.width,
          minSize: column.width,
          maxSize: column.width,
          muiTableBodyCellProps: {
            sx: {
              width: `${column.width}px`,
              minWidth: `${column.width}px`,
              maxWidth: `${column.width}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
          muiTableHeadCellProps: {
            align: column.align || 'left',
            sx: {
              width: `${column.width}px`,
              minWidth: `${column.width}px`,
              maxWidth: `${column.width}px`,
              whiteSpace: 'normal !important',
              wordWrap: 'break-word',
              overflow: 'visible !important',
              textOverflow: 'clip !important',
            },
          },
        }

        if (column.field === 'activa') {
          return {
            ...baseColumn,
            Cell: ({ row }) => <StatePublicOfferStateSwitch rowData={row.original} />,
            muiTableBodyCellProps: {
              align: 'center',
              sx: {
                ...baseColumn.muiTableBodyCellProps.sx,
              },
            },
            muiTableHeadCellProps: {
              align: 'center',
              sx: {
                ...baseColumn.muiTableHeadCellProps.sx,
              },
            },
          }
        }

        if (column.field === 'actions') {
          return {
            ...baseColumn,
            Cell: ({ row }) => <TableActions rowData={row.original} />,
            muiTableBodyCellProps: {
              align: 'center',
              sx: {
                ...baseColumn.muiTableBodyCellProps.sx,
              },
            },
            muiTableHeadCellProps: {
              align: 'center',
              sx: {
                ...baseColumn.muiTableHeadCellProps.sx,
              },
            },
          }
        }

        if (!column.hasOwnProperty('multiple') && column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              const displayValue = lookup[value] ?? value ?? '--'
              return (
                <Tooltip title={displayValue} placement="bottom-start" arrow>
                  <span>{displayValue}</span>
                </Tooltip>
              )
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const value = cell.getValue() ?? '--'
            return (
              <Tooltip title={value} placement="bottom-start" arrow>
                <span>{value}</span>
              </Tooltip>
            )
          },
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(
    () => publicOfferDataPreparation({ data, combos, intl }),
    [data, combos, intl]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      sorting: true,
      selection: true,
      showSelectAllCheckbox: true,
      tableLayout: 'auto',
      fixedColumns: {
        right: 2,
      },
      draggable: false,
    }),
    []
  )

  const handleRowClick = useCallback(
    (event, rowData) => {
      onClickTableRow(rowData?.id)
    },
    [onClickTableRow]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
    />
  )
}
